/**
 * 订单批量处理工具
 * 专门用于处理批量派送和批量失败场景
 */

import { DeliveryTaskOrderType } from '../graphql/types/DeliveryTaskOrderType';
import { PhotoType, PositionType } from '../services/PhotoService';
import DeliveryDataService, { FailedDeliveryOrder, FILE_UPLOAD_FAILED_KEY } from '../../../services/DeliveryDataService';
import { PhotoProcessingUtils } from './PhotoProcessingUtils';
import { GeoPosition } from '../services/GeolocationService';

// 订单处理上下文
export interface OrderProcessContext {
    /** 面单照片映射表 */
    labelPhotosMap: Map<string, string>;
    /** 位置及门牌照片数组 */
    photosArray: PhotoType[];
    /** 拍照时的位置 */
    position: GeoPosition | null;
    /** 完成时的位置 */
    completionPosition: GeoPosition | null;
    /** 完成时间 */
    dateString: string;
    /** 时间戳 */
    now: number;
    /** 异常原因代码（仅用于批量失败） */
    exceptionReasonCode?: string;
    /** 异常二级原因 code 列表（仅用于批量失败，如地址异常下选中的具体原因） */
    exceptionSubReasons?: string[] | null;
    /** 妥投方式 code */
    deliveryMethod?: string | null;
}

// 订单处理结果
export interface OrderProcessResult {
    trackingNumber: string;
    success: boolean;
    error?: Error;
}

// 订单处理器配置
export interface OrderProcessorConfig {
    /** 处理类型：delivered（派送成功）或 failed（派送失败） */
    type: 'delivered' | 'failed';
    /** 是否显示进度，默认 true */
    showProgress?: boolean;
    /** Worker 阈值，默认 100 */
    workerThreshold?: number;
    /** 批次大小，默认 20 */
    batchSize?: number;
}

// 批量处理进度回调
export type BatchProgressCallback = (processed: number, total: number) => void;

/**
 * 分批处理数据
 */
async function processBatch<T, R>(
    items: T[],
    processor: (item: T) => Promise<R>,
    batchSize: number,
    batchDelay: number,
    onProgress?: BatchProgressCallback
): Promise<R[]> {
    const results: R[] = [];
    let processedCount = 0;

    for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);

        const batchResults = await Promise.all(
            batch.map(async (item) => {
                const result = await processor(item);
                processedCount++;
                onProgress?.(processedCount, items.length);
                return result;
            })
        );

        results.push(...batchResults);

        // 给事件循环喘息的机会
        if (i + batchSize < items.length && batchDelay > 0) {
            await new Promise(resolve => setTimeout(resolve, batchDelay));
        }
    }

    return results;
}

/**
 * 订单批量处理器
 */
export class OrderBatchProcessor {
    /**
     * 批量处理订单（派送成功）
     */
    static async processDeliveredOrders(
        orders: DeliveryTaskOrderType[],
        context: OrderProcessContext,
        handleDelivered: (params: any, optimisticSubmission: boolean) => Promise<any>,
        config?: Partial<OrderProcessorConfig>,
        onProgress?: BatchProgressCallback
    ): Promise<OrderProcessResult[]> {
        const finalConfig: OrderProcessorConfig = {
            type: 'delivered',
            showProgress: true,
            workerThreshold: 100,
            batchSize: 20,
            ...config
        };

        console.log(`🚀 开始批量处理派送订单: ${orders.length} 单, 批次大小: ${finalConfig.batchSize}`);

        return processBatch(
            orders,
            async (order) => {
                try {
                    await this.processDeliveredOrder(order, context, handleDelivered);
                    return {
                        trackingNumber: order.orderWithAddress?.order?.trackingNumber || '',
                        success: true
                    };
                } catch (error) {
                    console.error(`❌ 处理订单失败: ${order.orderWithAddress?.order?.trackingNumber}`, error);
                    return {
                        trackingNumber: order.orderWithAddress?.order?.trackingNumber || '',
                        success: false,
                        error: error as Error
                    };
                }
            },
            finalConfig.batchSize!,
            10, // batchDelay
            onProgress
        );
    }

    /**
     * 批量处理订单（派送失败）
     */
    static async processFailedOrders(
        orders: DeliveryTaskOrderType[],
        context: OrderProcessContext,
        handleFailed: (params: any, optimisticSubmission: boolean) => Promise<any>,
        config?: Partial<OrderProcessorConfig>,
        onProgress?: BatchProgressCallback
    ): Promise<OrderProcessResult[]> {
        const finalConfig: OrderProcessorConfig = {
            type: 'failed',
            showProgress: true,
            workerThreshold: 100,
            batchSize: 20,
            ...config
        };

        console.log(`🚀 开始批量处理失败订单: ${orders.length} 单, 批次大小: ${finalConfig.batchSize}`);

        return processBatch(
            orders,
            async (order) => {
                try {
                    await this.processFailedOrder(order, context, handleFailed);
                    return {
                        trackingNumber: order.orderWithAddress?.order?.trackingNumber || '',
                        success: true
                    };
                } catch (error) {
                    console.error(`❌ 处理订单失败: ${order.orderWithAddress?.order?.trackingNumber}`, error);
                    return {
                        trackingNumber: order.orderWithAddress?.order?.trackingNumber || '',
                        success: false,
                        error: error as Error
                    };
                }
            },
            finalConfig.batchSize!,
            10, // batchDelay
            onProgress
        );
    }

    /**
     * 处理单个派送成功订单
     */
    private static async processDeliveredOrder(
        order: DeliveryTaskOrderType,
        context: OrderProcessContext,
        handleDelivered: (params: any, optimisticSubmission: boolean) => Promise<any>
    ): Promise<void> {
        const trackingNumber = order.orderWithAddress?.order?.trackingNumber || '';

        // 1. 获取该包裹的面单照片
        const labelPhoto = context.labelPhotosMap.get(trackingNumber);

        // 2. 组装该包裹的照片数组（面单照片 + 位置及门牌照片）
        const packagePhotosArray: PhotoType[] = [];

        // 添加面单照片（如果存在）
        if (labelPhoto) {
            packagePhotosArray.push({
                url: labelPhoto,
                objectKey: FILE_UPLOAD_FAILED_KEY,
                source: 'camera'
            });
        }

        // 添加位置及门牌照片
        context.photosArray.forEach(photo => {
            packagePhotosArray.push({
                url: photo.url,
                objectKey: FILE_UPLOAD_FAILED_KEY,
                capturedAt: photo.capturedAt,
                source: photo.source
            });
        });

        // 3. 执行乐观提交逻辑，更新内存中的订单状态
        await handleDelivered({
            trackingNumber: trackingNumber,
            location: {
                latitude: context.completionPosition?.latitude || 0.0,
                longitude: context.completionPosition?.longitude || 0.0
            },
            deliveredAt: context.dateString,
            podImageS3KeysJson: [],
            cameraPhotos: [],
            uploadPhotos: null,
            id: order.id
        }, true);

        // 4. 创建订单对象并保存到本地缓存
        const failedOrder: FailedDeliveryOrder = {
            trackingNumber: trackingNumber,
            updatedAt: context.now,
            deliveredAt: context.dateString,
            photos: PhotoProcessingUtils.processPhotosForSaving(
                packagePhotosArray,
                {} as Record<PositionType, PhotoType | undefined>,
                true
            ),
            location: context.position,
            stopNumber: order.stopNumber?.toString() || '0',
            subStopNumber: order.subStopNumber,
            id: order.id,
            type: 'delivered',
            completionLocation: context.completionPosition,
            ...(context.deliveryMethod ? { deliveryMethod: context.deliveryMethod } : {})
        };

        // 5. 保存到本地
        await DeliveryDataService.saveDeliveryOrder(failedOrder);
    }

    /**
     * 处理单个派送失败订单
     */
    private static async processFailedOrder(
        order: DeliveryTaskOrderType,
        context: OrderProcessContext,
        handleFailed: (params: any, optimisticSubmission: boolean) => Promise<any>
    ): Promise<void> {
        const trackingNumber = order.orderWithAddress?.order?.trackingNumber || '';

        // 1. 获取该包裹的面单照片
        const labelPhoto = context.labelPhotosMap.get(trackingNumber);

        // 2. 组装该包裹的照片数组（面单照片 + 位置及门牌照片）
        const packagePhotosArray: PhotoType[] = [];

        // 添加面单照片（如果存在）
        if (labelPhoto) {
            packagePhotosArray.push({
                url: labelPhoto,
                objectKey: FILE_UPLOAD_FAILED_KEY,
                source: 'camera'
            });
        }

        // 添加位置及门牌照片
        context.photosArray.forEach(photo => {
            packagePhotosArray.push({
                url: photo.url,
                objectKey: FILE_UPLOAD_FAILED_KEY,
                capturedAt: photo.capturedAt,
                source: photo.source
            });
        });

        // 3. 执行乐观提交逻辑，更新内存中的订单状态
        await handleFailed({
            trackingNumber: trackingNumber,
            location: {
                latitude: context.completionPosition?.latitude || 0.0,
                longitude: context.completionPosition?.longitude || 0.0
            },
            exceptionReasonCode: context.exceptionReasonCode,
            id: order.id,
            exceptionImageS3KeysJson: [],
            ...(context.exceptionSubReasons != null && context.exceptionSubReasons.length > 0
                ? { exceptionSubReasons: context.exceptionSubReasons }
                : {})
        }, true);

        // 4. 创建订单对象并保存到本地缓存
        const emptyPhotosRecord = {
            label: undefined,
            location: undefined,
            houseNumber: undefined
        } as Record<PositionType, PhotoType | undefined>;

        const failedOrder: FailedDeliveryOrder = {
            trackingNumber: trackingNumber,
            updatedAt: context.now,
            photos: PhotoProcessingUtils.processPhotosForSaving(
                packagePhotosArray,
                emptyPhotosRecord,
                true
            ),
            location: context.position,
            stopNumber: order.stopNumber?.toString() || '0',
            subStopNumber: order.subStopNumber,
            id: order.id,
            type: 'failed',
            exceptionReasonCode: context.exceptionReasonCode,
            completionLocation: context.completionPosition,
            ...(context.exceptionSubReasons != null && context.exceptionSubReasons.length > 0
                ? { exceptionSubReasons: context.exceptionSubReasons }
                : {})
        };

        // 5. 保存到本地
        await DeliveryDataService.saveDeliveryOrder(failedOrder);
    }
}
